import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Sparkles,
  HeartHandshake,
  Wind,
  Sprout,
  Flower,
  Hand,
  Music,
  Moon,
  BookOpen,
  Library,
  GraduationCap,
  CalendarDays,
  Users,
  UserCheck,
  Eye,
  Activity,
  Compass,
  type LucideIcon,
} from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselSwipeHint,
} from "@/components/ui/carousel";

import anxiousArt from "@/assets/feelings/feel-anxious.webp.asset.json";
import disconnectedArt from "@/assets/feelings/feel-disconnected.webp.asset.json";
import sadArt from "@/assets/feelings/feel-sad.webp.asset.json";
import triggeredArt from "@/assets/feelings/feel-triggered.webp.asset.json";
import connectionArt from "@/assets/feelings/feel-connection.webp.asset.json";
import understandArt from "@/assets/feelings/feel-understand.webp.asset.json";

export const Route = createFileRoute("/how-to-start")({
  head: () => ({
    meta: [
      { title: "How To Start — Recovery Trauma™" },
      {
        name: "description",
        content:
          "A gentle place to begin. Choose what you need right now and we'll guide you to the most relevant Recovery Trauma™ tools.",
      },
      { property: "og:title", content: "How To Start — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "A gentle place to begin. Choose what you need right now and we'll guide you to the most relevant Recovery Trauma™ tools.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/how-to-start" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/how-to-start" }],
  }),
  component: HowToStartPage,
});

type StartRoute =
  | "/check-in"
  | "/breathing"
  | "/grounding"
  | "/tapping"
  | "/somatic"
  | "/sounds"
  | "/bilateral"
  | "/sleep"
  | "/meditations"
  | "/journal"
  | "/dreams"
  | "/shadow-work"
  | "/library"
  | "/learn"
  | "/events"
  | "/community"
  | "/therapist-directory"
  | "/welcome";

type Tool = { label: string; to: StartRoute; icon: LucideIcon };

type Section = {
  id: string;
  emoji: string;
  title: string;
  description: string;
  art: { url: string };
  artAlt: string;
  tools: Tool[];
};

const SECTIONS: Section[] = [
  {
    id: "new-here",
    emoji: "💛",
    title: "I'm New Here",
    description: "A soft welcome. Start with a check-in and a few gentle tools.",
    art: connectionArt,
    artAlt: "Soft cartoon of a person being warmly welcomed",
    tools: [
      { label: "Daily Check-In", to: "/check-in", icon: HeartHandshake },
      { label: "Box Breathing", to: "/breathing", icon: Wind },
      { label: "5-4-3-2-1 Grounding", to: "/grounding", icon: Sprout },
      { label: "Welcome Guide", to: "/welcome", icon: Compass },
    ],
  },
  {
    id: "anxious",
    emoji: "🌬️",
    title: "I'm Feeling Anxious or Overwhelmed",
    description: "Calm your nervous system with gentle, body-based practices.",
    art: anxiousArt,
    artAlt: "Soft cartoon of a person breathing slowly with calm clouds",
    tools: [
      { label: "Box Breathing", to: "/breathing", icon: Wind },
      { label: "4-7-8 Breathing", to: "/breathing", icon: Wind },
      { label: "Grounding Tools", to: "/grounding", icon: Sprout },
      { label: "EFT Tapping", to: "/tapping", icon: Hand },
    ],
  },
  {
    id: "disconnected",
    emoji: "🌿",
    title: "I Feel Disconnected or Numb",
    description: "Soft ways to come back into your body and the present.",
    art: disconnectedArt,
    artAlt: "Soft cartoon of a person reconnecting with a leafy tree",
    tools: [
      { label: "Somatic Practices", to: "/somatic", icon: Flower },
      { label: "Grounding Tools", to: "/grounding", icon: Sprout },
      { label: "Bilateral Stimulation", to: "/bilateral", icon: Activity },
      { label: "Calming Sounds", to: "/sounds", icon: Music },
    ],
  },
  {
    id: "sleep",
    emoji: "😴",
    title: "I Need Better Sleep",
    description: "Soft rituals to rest a tired nervous system.",
    art: sadArt,
    artAlt: "Soft cartoon of a person wrapped in a blanket resting peacefully",
    tools: [
      { label: "Sleep & Rest", to: "/sleep", icon: Moon },
      { label: "Sleep Meditations", to: "/meditations", icon: Moon },
      { label: "Body Scan", to: "/grounding", icon: Activity },
      { label: "Relaxation Sounds", to: "/sounds", icon: Music },
    ],
  },
  {
    id: "process-emotions",
    emoji: "💛",
    title: "I Need to Process Emotions",
    description: "Honour what's surfacing with reflective, gentle tools.",
    art: understandArt,
    artAlt: "Soft cartoon of a person writing reflectively by candlelight",
    tools: [
      { label: "Recovery Journal", to: "/journal", icon: BookOpen },
      { label: "Dream Journal", to: "/dreams", icon: Moon },
      { label: "Shadow Work", to: "/shadow-work", icon: Eye },
    ],
  },
  {
    id: "learn",
    emoji: "📖",
    title: "I Want to Learn More About Healing",
    description: "Deepen your understanding with trauma-informed resources.",
    art: triggeredArt,
    artAlt: "Soft cartoon of a person reading peacefully by a warm lantern",
    tools: [
      { label: "Recovery Library", to: "/library", icon: Library },
      { label: "Learn Hub", to: "/learn", icon: GraduationCap },
      { label: "Live Workshops", to: "/events", icon: CalendarDays },
      { label: "Community", to: "/community", icon: Users },
    ],
  },
  {
    id: "professional",
    emoji: "🤝",
    title: "I Need Professional Support",
    description: "Find trauma-trained therapists and expert guidance.",
    art: connectionArt,
    artAlt: "Soft cartoon of a person being supported by a professional",
    tools: [
      { label: "Find a Therapist", to: "/therapist-directory", icon: UserCheck },
      { label: "Live Events", to: "/events", icon: CalendarDays },
      { label: "Therapist Articles", to: "/library", icon: Library },
      { label: "Community Support", to: "/community", icon: Users },
    ],
  },
];

function HowToStartPage() {
  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:py-10">
      <header className="mb-8 text-center sm:mb-10">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground sm:mb-4">
          <Sparkles className="h-3.5 w-3.5" /> How To Start
        </div>
        <h1 className="font-display text-3xl tracking-tight text-foreground sm:text-4xl md:text-5xl">
          💛 A gentle place to begin
        </h1>
        <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-muted-foreground sm:text-base">
          Choose what you need right now. We'll guide you straight to the most
          relevant Recovery Trauma™ tools — no need to navigate anything else.
        </p>
      </header>

      <Carousel
        opts={{ align: "start", loop: true }}
        className="w-full"
        aria-label="How to start"
      >
        <CarouselSwipeHint label="Swipe to explore" storageKey="how-to-start" />
        <CarouselContent className="-ms-5">
          {SECTIONS.map((s) => (
            <CarouselItem
              key={s.id}
              className="ps-5 basis-[88%] sm:basis-1/2 lg:basis-1/2"
            >
              <article
                className={[
                  "group relative flex h-full flex-col rounded-[2rem] p-6 sm:p-8 transition-all duration-300",
                  "hover:-translate-y-1",
                  "bg-gradient-to-br from-[oklch(0.97_0.025_85)] via-card to-[oklch(0.93_0.05_82)]",
                  "dark:from-[oklch(0.30_0.015_78)] dark:via-card dark:to-[oklch(0.34_0.04_80)]",
                  "border border-gold-light/60 dark:border-gold-light/40",
                  "shadow-[0_24px_60px_-32px_color-mix(in_oklab,var(--gold-deep)_45%,transparent)]",
                ].join(" ")}
              >
                <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
                  <span aria-hidden>{s.emoji}</span> A gentle place to start
                </div>

                <div className="mt-3 flex items-start gap-4">
                  <div
                    aria-hidden
                    className="relative shrink-0 h-24 w-24 sm:h-28 sm:w-28 rounded-3xl bg-[radial-gradient(ellipse_at_center,_var(--cream)_0%,_color-mix(in_oklab,var(--gold-light)_55%,var(--cream))_75%,_transparent_100%)] ring-1 ring-gold-light/50 overflow-hidden"
                  >
                    <img
                      src={s.art.url}
                      alt=""
                      loading="lazy"
                      width={224}
                      height={224}
                      className="absolute inset-0 h-full w-full object-contain p-1.5"
                    />
                  </div>
                  <div className="min-w-0 pt-1">
                    <h2 className="font-display text-xl leading-tight text-foreground sm:text-2xl">
                      {s.title}
                    </h2>
                  </div>
                </div>

                <p className="mt-4 text-sm leading-relaxed text-foreground/80 sm:text-base">
                  {s.description}
                </p>

                <div className="mt-auto pt-6">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
                    Recommended
                  </div>
                  <ul className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                    {s.tools.map((t) => {
                      const ToolIcon = t.icon;
                      return (
                        <li key={t.label}>
                          <Link
                            to={t.to}
                            aria-label={`Open ${t.label}`}
                            className={[
                              "flex h-full w-full items-start gap-2 rounded-xl border px-3 py-2.5 text-sm font-medium leading-snug shadow-sm transition-all",
                              "hover:-translate-y-0.5 hover:shadow-md hover:border-gold/60",
                              "bg-cream/80 border-gold-light/70 text-foreground",
                              "dark:bg-card/60 dark:border-gold-light/40",
                            ].join(" ")}
                          >
                            <ToolIcon className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold-deep)]" strokeWidth={2} />
                            <span className="min-w-0 flex-1 break-words">{t.label}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <span className="sr-only">{s.artAlt}</span>
              </article>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="hidden sm:flex -start-3" />
        <CarouselNext className="hidden sm:flex -end-3" />
      </Carousel>
    </div>
  );
}
